/**
 * Shared block components used by both:
 *   - ReportsTab.jsx (internal drawer)
 *   - PublicReport.jsx (public share URL)
 *
 * Keep the renderers dumb: they read directly from the `snapshot.blocks`
 * shape produced by /app/backend/reports_service.py, no data massaging.
 */
import { ExternalLink } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const fmtNum = (v) => (typeof v === "number" ? v.toLocaleString() : v ?? "—");

const toneMap = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lime: "bg-lime-50 text-lime-700 border-lime-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
};

export const Section = ({ title, subtitle, children, testId }) => (
  <section
    data-testid={testId}
    className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
  >
    <div className="mb-5">
      <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const KPI = ({ label, value, muted }) => (
  <div className={`border rounded-md p-3.5 ${muted ? "bg-white border-slate-100" : "bg-slate-50/70 border-slate-200"}`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 truncate" title={label}>{label}</div>
    <div className="font-mono text-lg font-semibold tabular-nums text-slate-900 leading-tight">{fmtNum(value)}</div>
  </div>
);

const ErrorNote = ({ err }) => <p className="text-xs text-rose-600">Error: {err}</p>;

// ─── 1) Acumulado / KPIs por perfil ─────────────────────────────────
export const AcumuladoBlock = ({ data }) => {
  if (!data) return null;
  const perfiles = data.perfiles || [];
  const kpisPP = data.kpis_por_perfil || {};
  const flat = data.acumulado || {};

  if (perfiles.length > 1) {
    return (
      <Section title="Acumulado por perfil · última semana" testId="rb-acumulado-dual">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {perfiles.map((p) => {
            const kpis = kpisPP[p] || {};
            return (
              <div key={p} className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="h-2 w-2 rounded-full bg-lime-500" />
                  <div className="text-sm font-semibold text-slate-900">{p}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(kpis).map(([k, v]) => <KPI key={k} label={k} value={v} muted />)}
                  {Object.keys(kpis).length === 0 && <div className="text-xs text-slate-400 italic col-span-full">Sin data.</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    );
  }
  return (
    <Section title="Acumulado del ciclo" testId="rb-acumulado">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {Object.entries(flat).map(([k, v]) => <KPI key={k} label={k} value={v} />)}
        {Object.keys(flat).length === 0 && <div className="text-xs text-slate-400 italic col-span-full">Sin data en la planilla.</div>}
      </div>
    </Section>
  );
};

// ─── 2) Acciones antepenúltima vs última semana ────────────────────
const ActionRow = ({ r }) => {
  const trendColor = r.delta > 0
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : r.delta < 0
    ? "text-rose-700 bg-rose-50 border-rose-200"
    : "text-slate-600 bg-slate-50 border-slate-200";
  const arrow = r.delta > 0 ? "↑" : r.delta < 0 ? "↓" : "·";
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="text-xs text-slate-700 min-w-0 flex-1 leading-snug">{r.accion}</div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <div className="font-mono text-sm font-bold tabular-nums text-slate-900 leading-none">{fmtNum(r.ultima)}</div>
          <div className="font-mono text-[10px] tabular-nums text-slate-400 mt-0.5">
            <span className="mr-1">antes</span>{fmtNum(r.anteultima)}
          </div>
        </div>
        <span className={`inline-flex items-center justify-center gap-0.5 text-[10px] font-mono font-semibold tabular-nums px-2 py-1 rounded border whitespace-nowrap min-w-[62px] ${trendColor}`}>
          <span className="opacity-80">{arrow}</span>
          {r.mom_pct == null
            ? (r.delta > 0 ? `+${r.delta}` : r.delta)
            : `${r.mom_pct > 0 ? "+" : ""}${r.mom_pct}%`}
        </span>
      </div>
    </div>
  );
};

const ActionsTable = ({ rows }) => (
  <div className="border border-slate-100 rounded-md divide-slate-100 bg-white">
    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
      <span>Acción</span>
      <span className="flex items-center gap-3">
        <span>Última semana</span>
        <span className="w-[62px] text-center">Δ MoM</span>
      </span>
    </div>
    <div className="px-3">
      {rows.map((r, i) => <ActionRow key={i} r={r} />)}
    </div>
  </div>
);

export const AccionesBlock = ({ data }) => {
  if (!data) return null;
  if (data.error) return <Section title="Acciones · antepenúltima vs última semana"><ErrorNote err={data.error} /></Section>;
  const perProfile = data.per_profile || {};
  const profileNames = Object.keys(perProfile);
  const showPerProfile = profileNames.length > 1;
  return (
    <Section
      title="Acciones · antepenúltima vs última semana"
      subtitle="Calculado automáticamente desde Skylead (ventana móvil de 14 días)"
      testId="rb-acciones"
    >
      {showPerProfile ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {profileNames.map((name) => (
            <div key={name} className="border border-slate-200 rounded-md p-4 bg-slate-50/40">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                <div className="text-sm font-semibold text-slate-900">{name}</div>
              </div>
              <ActionsTable rows={perProfile[name]} />
            </div>
          ))}
        </div>
      ) : (
        <ActionsTable rows={data.total || []} />
      )}
    </Section>
  );
};

// ─── 3) Estado del proyecto ─────────────────────────────────────────
export const EstadoProyectoBlock = ({ data }) => {
  if (!data) return null;
  const tone = toneMap[data.semaforo?.tone] || toneMap.slate;
  return (
    <Section title="Estado del proyecto" testId="rb-estado-proyecto">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Kick-off" value={data.kick_off || "—"} />
        <KPI label="Inicio outbound" value={data.outbound_start || "—"} />
        <KPI label="Finalización" value={data.finalizacion || "—"} />
        <KPI label="Días restantes" value={data.dias_restantes} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Objetivo reuniones" value={data.objetivo_reuniones} />
        <KPI label="Alcanzadas" value={data.reuniones_alcanzadas} />
        <KPI label="Proyectadas" value={data.reuniones_proyectadas} />
        <KPI label="Esperadas al día" value={data.reuniones_esperadas} />
      </div>
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Semáforo</span>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded border ${tone}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
          {data.semaforo?.label || "—"}
        </span>
        {data.meets_per_week_target > 0 && (
          <span className="text-xs text-slate-500 ml-1">
            Meta semanal: <span className="font-mono font-semibold text-slate-800">{data.meets_per_week_target}</span> reuniones
          </span>
        )}
      </div>
    </Section>
  );
};

// ─── 4) Reuniones por mes / campaña ─────────────────────────────────
const MeetingsTable = ({ title, rows, keyCol, valueCol }) => (
  <div>
    <div className="text-xs font-semibold text-slate-700 mb-2">{title}</div>
    <table className="w-full text-xs">
      <thead>
        <tr className="bg-lime-100/60 text-slate-800">
          <th className="px-3 py-2 text-left font-bold text-[11px] border border-lime-200">{keyCol}</th>
          <th className="px-3 py-2 text-right font-bold text-[11px] border border-lime-200">{valueCol}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="hover:bg-slate-50">
            <td className="px-3 py-1.5 border border-slate-200 text-slate-800">
              {r.label ? <><span>{r.mes}</span><span className="ml-2 font-mono text-[10px] text-slate-500">{`'${String(r.year).slice(-2)}`}</span></> : r.campaign}
            </td>
            <td className="px-3 py-1.5 border border-slate-200 text-right font-mono tabular-nums font-semibold text-slate-900">{r.value}</td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td colSpan={2} className="px-3 py-3 text-xs text-slate-400 italic text-center border border-slate-200">Sin reuniones registradas.</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const STATE_TONES = [
  { re: /realizada|meet\s*done/i, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { re: /calendarizad|scheduled/i, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  { re: /cotiz|quote|quotation/i, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { re: /esperando|coordinand|confirmation\s*waiting/i, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { re: /reprogram|reschedul/i, cls: "bg-orange-50 text-orange-700 border-orange-200" },
  { re: /cancel|no\s*avanza|declined|lost/i, cls: "bg-rose-50 text-rose-700 border-rose-200" },
];
const stateBadge = (estado) => {
  const s = (estado || "").trim();
  if (!s) return null;
  const tone = STATE_TONES.find((t) => t.re.test(s))?.cls || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-block text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap ${tone}`}>
      {s}
    </span>
  );
};

const MeetingRow = ({ r }) => (
  <li className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm text-slate-800 truncate">{r.empresa}</span>
        {stateBadge(r.estado)}
      </div>
      {(r.participante || r.puesto) && (
        <div className="text-[11px] text-slate-500 truncate mt-0.5">
          {[r.participante, r.puesto].filter(Boolean).join(" · ")}
        </div>
      )}
      {r.campaign && <div className="text-[10px] text-slate-400 italic truncate mt-0.5">Campaña: {r.campaign}</div>}
    </div>
    <div className="text-right whitespace-nowrap shrink-0">
      <div className="font-mono text-xs text-slate-700">{r.fecha}</div>
      {r.stage && <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 mt-0.5">{r.stage}</div>}
    </div>
  </li>
);

export const ReunionesBlock = ({ porMes, porCampaign, semana, pasadas }) => (
  <Section title="Reuniones" subtitle="Reuniones realizadas + Pedidos de cotización" testId="rb-reuniones">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
      <MeetingsTable title="Por mes" rows={porMes?.items || []} keyCol="Mes" valueCol="Reu / Coti" />
      <MeetingsTable title="Por campaña" rows={porCampaign?.items || []} keyCol="Campaña" valueCol="Meets / Cotiz" />
    </div>
    {(pasadas?.items || []).length > 0 && (
      <div className="mb-5">
        <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <span>Reuniones del período</span>
          <span className="text-[10px] font-normal text-slate-400">({pasadas.items.length})</span>
        </div>
        <ul className="space-y-0">
          {pasadas.items.map((r, i) => <MeetingRow key={i} r={r} />)}
        </ul>
      </div>
    )}
    {(semana?.items || []).length > 0 && (
      <div>
        <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-2">
          <span>Reuniones próximas · próximos 14 días</span>
          <span className="text-[10px] font-normal text-slate-400">({semana.items.length})</span>
        </div>
        <ul className="space-y-0">
          {semana.items.map((r, i) => <MeetingRow key={i} r={r} />)}
        </ul>
      </div>
    )}
  </Section>
);

// ─── 5) Ejecución BD totals ─────────────────────────────────────────
export const EjecucionBdBlock = ({ data }) => {
  if (!data) return null;
  if (data.error) return <Section title="Ejecución BD (outbound)"><ErrorNote err={data.error} /></Section>;
  return (
    <Section
      title="Ejecución BD (outbound)"
      subtitle={`Totales del período · ${(data.per_profile || []).length} perfil${(data.per_profile || []).length === 1 ? "" : "es"}`}
      testId="rb-ejecucion-bd"
    >
      <div className="grid grid-cols-3 gap-3 mb-4">
        <KPI label="Solicitudes" value={data.totals?.sent} />
        <KPI label="Nuevas conexiones" value={data.totals?.accepted} />
        <KPI label="Respuestas" value={data.totals?.replied} />
      </div>
      {(data.per_profile || []).length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 font-semibold">Perfil</th>
              <th className="py-2 text-right font-semibold">Enviados</th>
              <th className="py-2 text-right font-semibold">Aceptados</th>
              <th className="py-2 text-right font-semibold">Respuestas</th>
            </tr>
          </thead>
          <tbody>
            {data.per_profile.map((p, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="py-2">{p.name}</td>
                <td className="py-2 text-right font-mono">{fmtNum(p.sent)}</td>
                <td className="py-2 text-right font-mono">{fmtNum(p.accepted)}</td>
                <td className="py-2 text-right font-mono">{fmtNum(p.replied)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  );
};

// ─── Desempeño outbound · combined block (Ejecución BD KPIs + Acciones) ──
export const DesempenoOutboundBlock = ({ ejecucion, acciones }) => {
  if (!ejecucion && !acciones) return null;
  const hasEjec = ejecucion && !ejecucion.error;
  const hasAcc = acciones && !acciones.error;
  const perProfile = acciones?.per_profile || {};
  const profileNames = Object.keys(perProfile);
  const showPerProfile = profileNames.length > 1;
  return (
    <Section
      title="Desempeño outbound"
      subtitle={`Totales del período · comparativa vs. semana anterior · ${(ejecucion?.per_profile || []).length || profileNames.length} perfil${((ejecucion?.per_profile || []).length || profileNames.length) === 1 ? "" : "es"}`}
      testId="rb-desempeno-outbound"
    >
      {hasEjec && (
        <>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Totales del período</div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <KPI label="Solicitudes" value={ejecucion.totals?.sent} />
            <KPI label="Nuevas conexiones" value={ejecucion.totals?.accepted} />
            <KPI label="Respuestas" value={ejecucion.totals?.replied} />
          </div>
          {(ejecucion.per_profile || []).length > 0 && (
            <div className="mb-6 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="py-2 font-semibold">Perfil</th>
                    <th className="py-2 text-right font-semibold">Enviados</th>
                    <th className="py-2 text-right font-semibold">Aceptados</th>
                    <th className="py-2 text-right font-semibold">Respuestas</th>
                  </tr>
                </thead>
                <tbody>
                  {ejecucion.per_profile.map((p, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="py-2">{p.name}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{fmtNum(p.sent)}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{fmtNum(p.accepted)}</td>
                      <td className="py-2 text-right font-mono tabular-nums">{fmtNum(p.replied)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {ejecucion?.error && <ErrorNote err={ejecucion.error} />}
      {hasAcc && (
        <>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3 pt-2 border-t border-slate-100">
            Acciones · antepenúltima vs. última semana
            <span className="ml-2 text-[10px] normal-case tracking-normal text-slate-400 font-normal">
              (auto-calculado desde Skylead)
            </span>
          </div>
          {showPerProfile ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {profileNames.map((name) => (
                <div key={name} className="border border-slate-200 rounded-md p-4 bg-slate-50/40">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-2 w-2 rounded-full bg-sky-500" />
                    <div className="text-sm font-semibold text-slate-900">{name}</div>
                  </div>
                  <ActionsTable rows={perProfile[name]} />
                </div>
              ))}
            </div>
          ) : (
            <ActionsTable rows={acciones.total || []} />
          )}
        </>
      )}
      {acciones?.error && !hasAcc && <ErrorNote err={acciones.error} />}
    </Section>
  );
};

// ─── 6) Pendientes Notion ───────────────────────────────────────────
const VERTICAL_COLORS = {
  "SEO/AEO/GEO": "bg-rose-50 text-rose-700 border-rose-200",
  "Comunicación y Posicionamiento": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Desarrollo de negocios": "bg-amber-50 text-amber-700 border-amber-200",
  "Tech": "bg-slate-100 text-slate-700 border-slate-200",
};
const PRIORITY_COLORS = {
  "Alta": "bg-rose-50 text-rose-700 border-rose-200",
  "Media": "bg-amber-50 text-amber-700 border-amber-200",
  "Baja": "bg-slate-50 text-slate-500 border-slate-200",
};
const STATUS_DOTS = {
  "En curso": "bg-sky-500",
  "Listo": "bg-emerald-500",
  "Pendiente": "bg-amber-500",
};
const STATUS_BADGE = {
  "En curso": "bg-sky-50 text-sky-700 border-sky-200",
  "Listo": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Pendiente": "bg-amber-50 text-amber-700 border-amber-200",
};
const fmtShortDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short" }); }
  catch { return iso.slice(0, 10); }
};

const PendItem = ({ t }) => {
  const verticalClass = VERTICAL_COLORS[t.vertical] || "bg-slate-100 text-slate-600 border-slate-200";
  const priorityClass = PRIORITY_COLORS[t.priority] || "bg-slate-50 text-slate-500 border-slate-200";
  const statusDot = STATUS_DOTS[t.status] || "bg-slate-400";
  const statusBadge = STATUS_BADGE[t.status] || "bg-slate-50 text-slate-600 border-slate-200";
  const isBlocked = /^s[ií]$|^yes$/i.test((t.blocked || "").trim());
  return (
    <li className={`border rounded-md p-2.5 ${isBlocked ? "bg-rose-50/40 border-rose-200" : "border-slate-200"}`}>
      <div className="flex items-start gap-1.5">
        <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${statusDot} shrink-0`} />
        <div className="min-w-0 flex-1">
          {t.url ? (
            <a href={t.url} target="_blank" rel="noreferrer" className="text-xs font-medium leading-snug text-slate-900 hover:underline">
              {t.title || "(sin título)"}
            </a>
          ) : (
            <span className="text-xs font-medium leading-snug text-slate-900">{t.title || "(sin título)"}</span>
          )}
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {t.status && (
              <span className={`text-[9.5px] font-semibold px-1.5 py-0.5 rounded border ${statusBadge}`}>
                {t.status}
              </span>
            )}
            {isBlocked && (
              <span className="text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-200">
                🔒 Bloqueado
              </span>
            )}
            {t.vertical && (
              <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded border ${verticalClass}`}>
                {t.vertical}
              </span>
            )}
            {t.priority && (
              <span className={`text-[9.5px] font-medium px-1.5 py-0.5 rounded border ${priorityClass}`}>
                {t.priority}
              </span>
            )}
            {t.file?.url && (
              <a href={t.file.url} target="_blank" rel="noreferrer"
                 className="text-[9.5px] font-medium px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 inline-flex items-center gap-0.5 max-w-[160px]"
                 title={t.file.name || "Adjunto"}>
                📎 <span className="truncate">{t.file.name || "Adjunto"}</span>
              </a>
            )}
          </div>
          {(t.start_date || t.end_date) && (
            <div className="flex items-center gap-1.5 mt-1 text-[9.5px] text-slate-500">
              <span>📅</span>
              <span>{fmtShortDate(t.start_date)}</span>
              <span className="text-slate-300">→</span>
              <span>{fmtShortDate(t.end_date)}</span>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

const PendList = ({ label, items }) => (
  <div>
    <div className="text-xs font-semibold text-slate-700 mb-1.5">{label} ({items.length})</div>
    {items.length === 0 ? (
      <p className="text-xs text-slate-400 italic">Sin pendientes.</p>
    ) : (
      <ul className="space-y-1.5">
        {items.map((t, i) => <PendItem key={t.id || i} t={t} />)}
      </ul>
    )}
  </div>
);

export const PendientesBlock = ({ data }) => {
  if (!data) return null;
  if (data.error) return <Section title="Pendientes"><ErrorNote err={data.error} /></Section>;
  return (
    <Section title="Pendientes" testId="rb-pendientes">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PendList label="Del cliente" items={data.cliente || []} />
        <PendList label="De Narwhal" items={data.narwhal || []} />
      </div>
    </Section>
  );
};

// ─── 6.5) Bitácora Ads / CRO ───────────────────────────────────────
export const BitacoraBlock = ({ data }) => {
  if (!data) return null;
  if (data.error) return <Section title="Bitácora Ads · cambios aplicados"><ErrorNote err={data.error} /></Section>;
  const items = data.items || [];
  if (items.length === 0) return null;
  return (
    <Section
      title="Bitácora Ads · cambios aplicados"
      subtitle={data.in_window
        ? "Cambios y observaciones cargadas en el período"
        : `Sin entradas específicas del período · mostrando las últimas ${items.length} de la planilla`}
      testId="rb-bitacora"
    >
      <div className="space-y-3">
        {items.map((e, i) => (
          <div key={i} className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {e.period && (
                <span className="text-[10px] font-mono uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded-sm font-semibold">
                  {e.period}
                </span>
              )}
              {e.investment && (
                <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm font-semibold ml-auto">
                  {e.investment}
                </span>
              )}
            </div>
            {e.summary && (
              <div className="mb-2">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Resumen / Resultados</div>
                <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{e.summary}</p>
              </div>
            )}
            {e.observations && (
              <div className="mb-2">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Observaciones de control</div>
                <p className="text-[11px] text-slate-600 leading-relaxed">{e.observations}</p>
              </div>
            )}
            {e.pending && (
              <div className="mt-2 pt-2 border-t border-slate-200">
                <div className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Pendientes</div>
                <p className="text-[11px] text-amber-800 leading-relaxed">{e.pending}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
};

// ─── 7) Contenidos publicados ───────────────────────────────────────
export const ContenidosBlock = ({ data, weekly }) => {
  if (!data) return null;
  if (data.error) return <Section title="Contenidos publicados"><ErrorNote err={data.error} /></Section>;
  const items = data.items || [];
  // Extract a friendly domain label from a URL (e.g. "linkedin.com", "instagram.com")
  const shortLink = (url) => {
    if (!url) return "";
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      const path = u.pathname.replace(/\/$/, "").split("/").filter(Boolean);
      const tail = path.length > 0 ? path[path.length - 1].slice(0, 40) : "";
      return tail ? `${host} · ${tail}` : host;
    } catch {
      return url.slice(0, 60);
    }
  };
  return (
    <Section
      title="Contenidos publicados"
      subtitle={weekly
        ? `${items.length} publicación${items.length === 1 ? "" : "es"} · LinkedIn tarda 2-3 semanas en madurar métricas (performance en el reporte mensual)`
        : `${items.length} publicación${items.length === 1 ? "" : "es"} en el período`}
      testId="rb-contenidos"
    >
      {items.length === 0 ? (
        <p className="text-xs text-slate-500 italic">Sin publicaciones en el rango.</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((p, i) => {
            const displayText = p.title || p.url;
            return (
              <li key={i} className="flex items-start gap-3 pb-2.5 border-b border-slate-100 last:border-0 last:pb-0">
                <div className="text-[11px] font-mono text-slate-500 tabular-nums whitespace-nowrap mt-0.5 w-20 shrink-0">
                  {p.date}
                </div>
                <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                  {p.content_type && (
                    <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap">
                      {p.content_type}
                    </span>
                  )}
                  {p.url ? (
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-sky-700 hover:text-sky-900 hover:underline min-w-0"
                      title={p.url}
                    >
                      <span className="truncate">{displayText || shortLink(p.url)}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  ) : (
                    <span className="text-xs text-slate-700 truncate">{displayText || <span className="italic text-slate-400">(sin link)</span>}</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
};

// ─── 8) Dashboard mensual · lectura 1:1 de la planilla del cliente ─
const NATURALEZA_TONES = {
  "Ejecución": "bg-sky-50 text-sky-700 border-sky-200",
  "Resultado": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Impacto económico": "bg-amber-50 text-amber-700 border-amber-200",
  "Contexto": "bg-slate-100 text-slate-600 border-slate-200",
};

const NaturalezaBadge = ({ value }) => {
  if (!value) return null;
  const tone = NATURALEZA_TONES[value] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-block text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap ${tone}`}>
      {value}
    </span>
  );
};

export const DashboardMensualBlock = ({ data }) => {
  if (!data) return null;
  if (data.error) return <Section title="Dashboard mensual"><ErrorNote err={data.error} /></Section>;
  const months = data.months || [];
  const areas = data.areas || [];
  if (months.length === 0 || areas.length === 0) return null;

  return (
    <Section
      title="Operación & Métricas"
      subtitle={`Últimos ${months.length} meses · lectura directa de la planilla del cliente`}
      testId="rb-dashboard-mensual"
    >
      <div className="space-y-8">
        {areas.map((area, ai) => (
          <div key={ai}>
            <div className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-slate-900" />
              {area.area}
            </div>
            {area.subsections.map((sub, si) => (
              <div key={si} className="mb-5 last:mb-0">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2 pl-3.5">{sub.frente}</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold w-1/3">Métrica</th>
                        <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">Naturaleza</th>
                        <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">Objetivo</th>
                        {months.map((m) => (
                          <th key={m.key} className="text-right py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
                            {m.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sub.rows.map((row, ri) => {
                        const nums = row.values.map((v) => v.num).filter((n) => typeof n === "number");
                        const maxVal = nums.length ? Math.max(...nums) : null;
                        return (
                          <tr key={ri} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/40">
                            <td className="py-2 px-3 text-slate-800 leading-tight">
                              {row.metric}
                              {row.plazo && row.plazo !== "—" && (
                                <div className="text-[9px] text-slate-400 italic mt-0.5">{row.plazo}</div>
                              )}
                            </td>
                            <td className="py-2 px-2"><NaturalezaBadge value={row.naturaleza} /></td>
                            <td className="py-2 px-2 text-right text-[11px] text-slate-500 whitespace-nowrap">
                              {row.objetivo && row.objetivo !== "-" ? row.objetivo : ""}
                            </td>
                            {row.values.map((cell, ci) => {
                              const isMax = typeof cell.num === "number" && cell.num === maxVal && cell.num > 0;
                              const isEmpty = !cell.raw || cell.raw === "-";
                              return (
                                <td
                                  key={ci}
                                  className={`py-2 px-3 text-right font-mono tabular-nums whitespace-nowrap ${
                                    isEmpty ? "text-slate-300" :
                                    isMax ? "font-semibold text-emerald-700" : "text-slate-700"
                                  }`}
                                >
                                  {isEmpty ? "—" : cell.raw}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Section>
  );
};

const NotionHechasList = ({ label, rows }) => (
  <div>
    <div className="text-xs font-semibold text-slate-700 mb-2">{label} ({rows.length})</div>
    {rows.length === 0 ? (
      <p className="text-xs text-slate-400 italic">Sin tareas cerradas.</p>
    ) : (
      <ul className="space-y-1.5">
        {rows.map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
            <div className="flex-1 min-w-0">
              {t.url ? (
                <a href={t.url} target="_blank" rel="noreferrer" className="hover:underline text-slate-800">{t.title}</a>
              ) : (
                <span className="text-slate-800">{t.title}</span>
              )}
              <span className="ml-2 font-mono text-[10px] text-slate-400">{t.end_date}</span>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

// ─── 9) Tareas hechas en el mes (solo monthly) ─────────────────────
export const NotionHechasBlock = ({ data }) => {
  if (!data) return null;
  const items = data.items || [];
  if (items.length === 0) return null;
  const cliente = items.filter((t) => t.side === "Cliente");
  const narwhal = items.filter((t) => t.side !== "Cliente");
  return (
    <Section
      title="Tareas cerradas en el mes"
      subtitle="Notion · marcadas como Done con fecha de finalización dentro del período"
      testId="rb-notion-hechas"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NotionHechasList label="Del cliente" rows={cliente} />
        <NotionHechasList label="De Narwhal" rows={narwhal} />
      </div>
    </Section>
  );
};

// ─── 10) Outreach chart (solo monthly) ─────────────────────────────
const buildChartData = (labels, series) => {
  return labels.map((label, i) => {
    const row = { day: label };
    Object.entries(series).forEach(([k, v]) => { row[k] = v[i] ?? 0; });
    return row;
  });
};

const chartTooltipStyle = {
  contentStyle: { fontSize: 11, borderRadius: 6, border: "1px solid #e2e8f0" },
  labelStyle: { color: "#334155", fontWeight: 600 },
};

const KpiChip = ({ label, value, tone }) => {
  const tones = {
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={`border rounded-md px-3 py-2 ${tones[tone] || tones.sky}`}>
      <div className="text-[9px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="font-mono text-lg font-bold tabular-nums leading-none mt-0.5">{fmtNum(value)}</div>
    </div>
  );
};

export const OutreachChartBlock = ({ data, periodStart, periodEnd }) => {
  if (!data) return null;
  if (data.error) return <Section title="Outreach del mes"><ErrorNote err={data.error} /></Section>;
  const days = data.days || [];
  if (days.length === 0) return null;
  const invitesData = buildChartData(days, {
    Enviadas: data.invites?.sent || [],
    Aceptadas: data.invites?.accepted || [],
    Respuestas: data.invites?.replied || [],
  });
  const emailsData = buildChartData(days, {
    Enviados: data.emails?.sent || [],
    Descubiertos: data.emails?.verified || [],
    Abiertos: data.emails?.opened || [],
  });
  const t = data.totals || {};
  const accRate = t.invites_sent ? Math.round((t.invites_accepted / t.invites_sent) * 100) : 0;

  return (
    <Section
      title="Outreach del mes"
      subtitle={`Volumen diario · ${periodStart || ""} → ${periodEnd || ""}`}
      testId="rb-outreach-chart"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiChip label="Invites" value={t.invites_sent} tone="sky" />
        <KpiChip label={`Aceptación ${accRate}%`} value={t.invites_accepted} tone="emerald" />
        <KpiChip label="Respuestas" value={t.invites_replied} tone="violet" />
        <KpiChip label="Emails enviados" value={t.emails_sent} tone="amber" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2">Invitaciones · aceptaciones · respuestas</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={invitesData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} tickMargin={6} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={32} />
                <Tooltip {...chartTooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="line" />
                <Line type="monotone" dataKey="Enviadas" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Aceptadas" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Respuestas" stroke="#8b5cf6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-2">Emails · descubiertos · enviados · abiertos</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={emailsData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748b" }} tickMargin={6} />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={32} />
                <Tooltip {...chartTooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="line" />
                <Line type="monotone" dataKey="Enviados" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Descubiertos" stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="Abiertos" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </Section>
  );
};

// ─── 11) Reuniones trend chart (solo monthly) ──────────────────────
export const ReunionesTrendBlock = ({ data }) => {
  if (!data) return null;
  const series = data.series || [];
  if (series.length === 0) return null;
  const total = series.reduce((s, r) => s + (r.value || 0), 0);
  const last = series[series.length - 1]?.value || 0;
  const prev = series[series.length - 2]?.value || 0;
  const delta = last - prev;
  const deltaColor = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-500";
  return (
    <Section
      title="Reuniones · trend últimos 6 meses"
      subtitle={`Reuniones realizadas + pedidos de cotización · total ${total}`}
      testId="rb-reuniones-trend"
    >
      <div className="flex items-baseline gap-3 mb-3">
        <div className="font-mono text-3xl font-bold text-slate-900 tabular-nums">{last}</div>
        <div className="text-xs text-slate-500">este mes</div>
        <div className={`text-xs font-mono ${deltaColor}`}>
          {delta > 0 ? "↑ +" : delta < 0 ? "↓ " : "·"}{delta} vs mes anterior
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} tickMargin={6} />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={32} allowDecimals={false} />
            <Tooltip {...chartTooltipStyle} />
            <Line type="monotone" dataKey="value" name="Reuniones" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: "#0f172a" }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
};

// ─── Master renderer used by both drawer + public page ──────────────
// Weekly order:  Pendientes → Contenidos → Bitácora → Desempeño out → Reuniones
// Monthly order: Operación & Métricas → Outreach chart → Reuniones trend
//                → Tareas cerradas → Pendientes → Contenidos → Reuniones (past+upcoming)
//                → Bitácora
export function ReportBody({ snapshot, kind, periodStart, periodEnd }) {
  const b = snapshot?.blocks || {};
  const isWeekly = kind === "weekly";
  const isMonthly = kind === "monthly";
  if (isMonthly) {
    return (
      <div className="space-y-6">
        <DashboardMensualBlock data={b.dashboard_mensual} />
        <OutreachChartBlock data={b.outreach_chart} periodStart={periodStart} periodEnd={periodEnd} />
        <ReunionesTrendBlock data={b.reuniones_trend} />
        <NotionHechasBlock data={b.notion_hechas} />
        <PendientesBlock data={b.pendientes} />
        <ContenidosBlock data={b.contenidos} weekly={false} />
        <ReunionesBlock porMes={b.reuniones_mes} porCampaign={b.reuniones_campaign} semana={b.reuniones_semana} pasadas={b.reuniones_pasadas} />
        <BitacoraBlock data={b.bitacora} />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <PendientesBlock data={b.pendientes} />
      <ContenidosBlock data={b.contenidos} weekly={isWeekly} />
      <BitacoraBlock data={b.bitacora} />
      <DesempenoOutboundBlock ejecucion={b.ejecucion_bd} acciones={b.acciones} />
      <ReunionesBlock porMes={b.reuniones_mes} porCampaign={b.reuniones_campaign} semana={b.reuniones_semana} pasadas={b.reuniones_pasadas} />
    </div>
  );
}
